// Типы боя, общие для клиента и сервера: приёмы, события для анимации, таблица anim_ms.
// Формулы здесь НЕТ — она в backend/combat (ADR-0003). Источники: tech/04 §3–§8, ГД 05 §6.
import { z } from 'zod';
import { BossId, BossMoveId, EpochMs, Hex32, Hex64, ItemDefId, PublicId, Rarity, Uuid7, strict } from './common.js';

/**
 * ID приёмов игрока — ГД 05 §6.1 («ID постоянные»), narrative/07, ux/13: `strike`, `kit`.
 * В tech/03 §6 и tech/04 §3 написано `hit`/`medkit` — расхождение, решение каркаса Р-01: берём ID геймдизайна.
 */
export const PLAYER_MOVES = ['strike', 'wrench', 'chem', 'kit', 'torch', 'jack', 'weld'] as const;
export const MoveId = z.enum(PLAYER_MOVES);
export type MoveId = z.infer<typeof MoveId>;

export const BattleMode = z.enum(['solo', 'coop', 'world']);
export type BattleMode = z.infer<typeof BattleMode>;

/** Исходы захода (tech/04 §6, tech/02 §7 battles.outcome). */
export const BattleOutcome = z.enum(['boss_defeated', 'player_defeated', 'rounds_over', 'retreat', 'boss_gone', 'void']);
export type BattleOutcome = z.infer<typeof BattleOutcome>;

export const MoveState = strict({
  moveId: MoveId,
  cooldown: z.int().nonnegative(),
  ready: z.boolean(),
  dieselCost: z.int().nonnegative(),
});

export const Telegraph = strict({ moveId: BossMoveId, nextRound: z.int().min(1).max(10) });

const Target = z.enum(['boss', 'player']);

/**
 * События ответа хода (tech/04 §4.1). В спецификации — примеры; полный список типов — решение каркаса Р-03:
 * добавлены `effect_on`, `heal`, `shield`, `boss_skip`, `boss_gone` (04 §5.1), чтобы клиент мог проиграть
 * все архетипы D1 (ГД 06 §4а) только по событиям.
 */
export const BattleEvent = z.discriminatedUnion('e', [
  strict({ e: z.literal('player_move'), moveId: MoveId, auto: z.boolean().optional() }),
  strict({
    e: z.literal('hit_boss'),
    dmg: z.int().nonnegative(),
    crit: z.boolean(),
    miss: z.boolean(),
    /** Сколько реально ушло в HP (после щита, ворот урона и обрезки по остатку). */
    effective: z.int().nonnegative(),
    /** Урон ушёл в щит босса (ГД 05 §7.1). */
    shielded: z.int().nonnegative().optional(),
    /** Сработали ворота урона «Босс упирается» (ГД 05 §3 п. 9). */
    gated: z.boolean().optional(),
  }),
  strict({ e: z.literal('boss_move'), moveId: BossMoveId, fast: z.boolean() }),
  strict({ e: z.literal('hit_player'), dmg: z.int().nonnegative(), dodged: z.boolean() }),
  strict({
    e: z.literal('effect_tick'),
    effect: z.string().min(1).max(32),
    target: Target,
    dmg: z.int().nonnegative(),
    effective: z.int().nonnegative().optional(),
  }),
  strict({ e: z.literal('effect_on'), effect: z.string().min(1).max(32), target: Target, turns: z.int().nonnegative() }),
  strict({ e: z.literal('heal'), target: Target, amount: z.int().nonnegative() }),
  strict({ e: z.literal('shield'), target: z.literal('boss'), amount: z.int().nonnegative() }),
  strict({ e: z.literal('cooldown'), moveId: MoveId, turns: z.int().nonnegative() }),
  strict({ e: z.literal('telegraph'), moveId: BossMoveId, nextRound: z.int().min(1).max(10) }),
  strict({ e: z.literal('boss_skip'), reason: z.enum(['jack', 'stagger']) }),
  strict({ e: z.literal('boss_gone'), by: strict({ publicId: PublicId, name: z.string() }) }),
]);
export type BattleEvent = z.infer<typeof BattleEvent>;
export type BattleEventType = BattleEvent['e'];

/**
 * Длительность анимации событий, мс (tech/04 §4.3: «таблица anim_ms … совпадает с клиентской (общий модуль)»).
 * Сервер считает по ней `turnDeadlineAt`, клиент — длительность проигрывания. Числа — `[старт]` каркаса (Р-06):
 * замах 1.4 с (ГД 05 §5.2 п. 3), раунд ≈ 2.8 с (ADR-0011). Владелец чисел — ГД/UX (E03-T08).
 */
export const ANIM_MS: Readonly<Record<BattleEventType, number>> = {
  player_move: 1400,
  hit_boss: 400,
  boss_move: 600,
  hit_player: 400,
  effect_tick: 300,
  effect_on: 200,
  heal: 300,
  shield: 300,
  cooldown: 0,
  telegraph: 300,
  boss_skip: 400,
  boss_gone: 800,
};

export function animMs(events: readonly Pick<BattleEvent, 'e'>[]): number {
  return events.reduce((sum, ev) => sum + ANIM_MS[ev.e], 0);
}

/** Тайминги захода `[старт]` (tech/04 §9; ГД 05 §5.2). Источник истины на сервере — конфиг `combat.*`. */
export const COMBAT_TIMING = {
  maxRounds: 10,
  choiceTimerMs: 4500,
  calmChoiceTimerMs: 9000,
  turnGraceMs: 3000,
  minTurnIntervalMs: 2200,
  idleAutoplayS: 60,
  battleMaxS: 240,
  entryEnergy: 10,
  clientTurnTimeoutMs: 4000,
} as const;

/** Итог захода (tech/04 §6 `BattleResult`). */
export const BattleResult = strict({
  battleId: Uuid7,
  outcome: BattleOutcome,
  rounds: z.int().min(0).max(10),
  durationMs: z.int().nonnegative(),
  passive: z.boolean(),
  damage: strict({ raw: z.int().nonnegative(), effective: z.int().nonnegative() }),
  rewards: strict({
    xp: z.int().nonnegative(),
    nuts: z.int().nonnegative(),
    scrap: z.int().nonnegative(),
    items: z.array(strict({ itemId: z.string(), defId: ItemDefId, rarity: Rarity })),
    oddsVersion: z.int().nonnegative(),
  }),
  energyRefund: z.int().nonnegative(),
  levelUp: strict({ from: z.int(), to: z.int() }).nullable(),
  contribution: strict({
    iid: Uuid7,
    damage: z.int().nonnegative(),
    rank: z.int().positive(),
    qualified: z.boolean(),
    bossHp: z.int().nonnegative(),
  }).nullable(),
  bossRewardPending: z.boolean(),
  seed: Hex32,
  seedCommit: Hex64,
  cfgVer: z.int().nonnegative(),
});
export type BattleResult = z.infer<typeof BattleResult>;

/** Ответ на старт захода (tech/04 §3 `BattleStart`). */
export const BattleStart = strict({
  battleId: Uuid7,
  boss: strict({
    bossId: BossId,
    mode: BattleMode,
    iid: Uuid7.nullable(),
    hp: z.int().nonnegative(),
    hpMax: z.int().positive(),
    telegraph: Telegraph.nullable(),
  }),
  player: strict({ hp: z.int().nonnegative(), hpMax: z.int().positive() }),
  playerFirst: z.boolean(),
  openingEvents: z.array(BattleEvent),
  moves: z.array(MoveState),
  diesel: z.int().nonnegative(),
  fullTank: z.boolean(),
  round: z.int().min(1).max(10),
  turnNo: z.int().nonnegative(),
  maxRounds: z.literal(10),
  choiceTimerMs: z.int().positive(),
  calm: z.boolean(),
  turnDeadlineAt: EpochMs,
  seedCommit: Hex64,
  cfgVer: z.int().nonnegative(),
  contentVer: z.string().nullable(),
  energy: strict({ value: z.int().nonnegative(), nextAt: z.iso.datetime().nullable() }),
  /** Уровень карточки звезды сезона (03 §6.1); у районных боссов — нет. */
  lStar: z.int().positive().optional(),
});
export type BattleStart = z.infer<typeof BattleStart>;

/** Ответ на ход (tech/04 §4.1). `state.moves` — полный список приёмов (решение Р-03). */
export const TurnResult = strict({
  turnNo: z.int().positive(),
  round: z.int().min(1).max(10),
  events: z.array(BattleEvent),
  state: strict({
    player: strict({ hp: z.int().nonnegative() }),
    boss: strict({ hp: z.int().nonnegative(), hpMax: z.int().positive() }),
    moves: z.array(MoveState),
    diesel: z.int().nonnegative(),
    turnDeadlineAt: EpochMs,
  }),
  over: BattleResult.nullable(),
});
export type TurnResult = z.infer<typeof TurnResult>;

/**
 * Состояние активного захода для восстановления (tech/04 §10 п. 2: «как BattleStart + последний TurnResult,
 * turnNo, round»). Форма — решение каркаса Р-03: текущие числа лежат в полях BattleStart, плюс `lastTurn`.
 */
export const BattleState = BattleStart.extend({ lastTurn: TurnResult.nullable() });
export type BattleState = z.infer<typeof BattleState>;
